import pino from 'pino';
import { config } from '../next-logger.config';
import redactor from './redactor';
import isEnvDevelopment from '../lib/isEnvDevelopment';

const LOGGER_NAME_KEY = 'nw_logger_name';

const BASE_LOGGER = pino(config);

export class Logger {
  private _pinoLogger: pino.Logger;

  private _shouldLog: boolean;

  constructor(pinoLogger: pino.Logger, shouldLog?: boolean) {
    this._pinoLogger = pinoLogger;
    this._shouldLog = shouldLog ?? true;
  }

  public static logger(
    options?: { tags?: Record<string, string> },
    shouldLog?: boolean
  ): Logger {
    const pinoLogger = BASE_LOGGER.child({ ...options?.tags });
    return new Logger(pinoLogger, shouldLog);
  }

  private static formatMessage({
    loggerName,
    message,
    context,
  }: {
    loggerName: string;
    message: string;
    context: Record<string, any> | null;
  }): Record<string, any> {
    return {
      [LOGGER_NAME_KEY]: isEnvDevelopment()
        ? loggerName
        : redactor.apply(loggerName),
      message: isEnvDevelopment() ? message : redactor.apply(message),
      ...(context && {
        context: isEnvDevelopment() ? context : redactor.apply(context),
      }),
    };
  }

  public error(
    loggerName: string,
    message: string,
    context: Record<string, any> | null = null
  ): void {
    if (!this._shouldLog) return;
    this._pinoLogger.error(
      Logger.formatMessage({
        loggerName,
        message,
        context,
      })
    );
  }

  public warn(
    loggerName: string,
    message: string,
    context: Record<string, any> | null = null
  ): void {
    if (!this._shouldLog) return;
    this._pinoLogger.warn(
      Logger.formatMessage({
        loggerName,
        message,
        context,
      })
    );
  }

  public info(
    loggerName: string,
    message: string,
    context: Record<string, any> | null = null
  ): void {
    if (!this._shouldLog) return;
    this._pinoLogger.info(
      Logger.formatMessage({
        loggerName,
        message,
        context,
      })
    );
  }

  public debug(
    loggerName: string,
    message: string,
    context: Record<string, any> | null = null,
    forceLog: boolean = false
  ): void {
    if (!forceLog && !this._shouldLog) return;
    this._pinoLogger.debug(
      Logger.formatMessage({
        loggerName,
        message,
        context,
      })
    );
  }
}
